/**
 * Module registry for the profession landing page (/szakemberek/:profession).
 *
 * Two kinds of modules can appear here:
 *
 *  1. Profession-specific modules — wrappers around the existing hardcoded
 *     sections of the profession page. They consume the current profession
 *     via `inject("profession")` (set up in the profession page's `provide()`),
 *     so no extra context plumbing is needed.
 *
 *  2. Shared front-page modules — modules that live in `frontPageModules.js`
 *     but can also be slotted into the profession page. Their content is
 *     inherited from the active front-page version at render time. The list
 *     of shared slugs is configured in `sharedFrontPageModulesOnProfessionPage.js`.
 */

import {
  FRONT_PAGE_MODULES,
  getBaseSlug as getFrontPageBaseSlug,
} from "~/config/frontPageModules";
import { SHARED_FRONT_PAGE_MODULE_SLUGS } from "~/config/sharedFrontPageModulesOnProfessionPage";

export const PROFESSION_PAGE_OWN_MODULES = {
  "profession-hero-banner": () =>
    import(
      "~/components/_refactored/professionPage/profession-page-modules/ProfessionHeroBannerModule.vue"
    ),
  "profession-section-heading": () =>
    import(
      "~/components/_refactored/professionPage/profession-page-modules/ProfessionSectionHeadingModule.vue"
    ),
  "profession-people-swiper": () =>
    import(
      "~/components/_refactored/professionPage/profession-page-modules/ProfessionPeopleSwiperModule.vue"
    ),
  "profession-advice-block": () =>
    import(
      "~/components/_refactored/professionPage/profession-page-modules/ProfessionAdviceBlockModule.vue"
    ),
  "profession-info-cards": () =>
    import(
      "~/components/_refactored/professionPage/profession-page-modules/ProfessionInfoCardsModule.vue"
    ),
  "profession-task-cards": () =>
    import(
      "~/components/_refactored/professionPage/profession-page-modules/ProfessionTaskCardsModule.vue"
    ),
  "profession-city-tabs": () =>
    import(
      "~/components/_refactored/professionPage/profession-page-modules/ProfessionCityTabsModule.vue"
    ),
  "profession-faq": () =>
    import(
      "~/components/_refactored/professionPage/profession-page-modules/ProfessionFaqModule.vue"
    ),
  "profession-blog": () =>
    import(
      "~/components/_refactored/professionPage/profession-page-modules/ProfessionBlogModule.vue"
    ),
};

/**
 * Slugs that should render full-bleed (extending past the page container)
 * and visually sit "above" a white card containing the rest of the modules.
 */
export const FULL_BLEED_HERO_SLUGS = new Set(["profession-hero-banner"]);

/**
 * Profession page module registry.
 *
 * Resolves slug → async component loader. For shared front-page slugs, it
 * delegates to the front-page registry so we always render the same component
 * the front page uses.
 */
export const PROFESSION_PAGE_MODULES = new Proxy(PROFESSION_PAGE_OWN_MODULES, {
  get(target, slug) {
    if (slug in target) return target[slug];
    if (typeof slug !== "string") return undefined;
    if (SHARED_FRONT_PAGE_MODULE_SLUGS.has(slug)) {
      return FRONT_PAGE_MODULES[slug];
    }
    const baseFrontSlug = getFrontPageBaseSlug(slug);
    if (SHARED_FRONT_PAGE_MODULE_SLUGS.has(baseFrontSlug)) {
      return FRONT_PAGE_MODULES[baseFrontSlug];
    }
    return undefined;
  },
  has(target, slug) {
    if (slug in target) return true;
    if (typeof slug !== "string") return false;
    return (
      SHARED_FRONT_PAGE_MODULE_SLUGS.has(slug) ||
      SHARED_FRONT_PAGE_MODULE_SLUGS.has(getFrontPageBaseSlug(slug))
    );
  },
  ownKeys(target) {
    return Array.from(
      new Set([
        ...Object.keys(target),
        ...Array.from(SHARED_FRONT_PAGE_MODULE_SLUGS),
      ])
    );
  },
  getOwnPropertyDescriptor() {
    return { enumerable: true, configurable: true };
  },
});

/**
 * Slugs that can appear more than once on the profession page.
 * Mirrors the front-page convention.
 */
export const MULTI_INSTANCE_SLUGS = new Set(["rich-text"]);

export const getBaseSlug = (slug) => {
  if (MULTI_INSTANCE_SLUGS.has(slug)) return slug;
  const match = slug.match(/^(.+)-(\d+)$/);
  if (match && MULTI_INSTANCE_SLUGS.has(match[1])) return match[1];
  return slug;
};

/**
 * Default ordering used as a fallback when no active config exists in the API
 * (and as seed data for the global default the backend can pre-populate).
 */
export const DEFAULT_PROFESSION_PAGE_MODULES = [
  {
    slug: "profession-hero-banner",
    label: "Hero Banner",
    position: 0,
    row: 0,
    isVisible: true,
  },
  {
    slug: "profession-section-heading",
    label: "Section Heading",
    position: 1,
    row: 1,
    isVisible: true,
  },
  {
    slug: "profession-people-swiper",
    label: "People Swiper",
    position: 2,
    row: 2,
    isVisible: true,
  },
  {
    slug: "profession-info-cards",
    label: "Info Cards",
    position: 3,
    row: 3,
    isVisible: true,
  },
  {
    slug: "profession-task-cards",
    label: "Task Cards",
    position: 4,
    row: 4,
    isVisible: true,
  },
  {
    slug: "profession-advice-block",
    label: "Advice Block",
    position: 5,
    row: 5,
    isVisible: true,
  },
  {
    slug: "profession-city-tabs",
    label: "City Tabs",
    position: 6,
    row: 6,
    isVisible: true,
  },
  {
    slug: "profession-faq",
    label: "FAQ",
    position: 7,
    row: 7,
    isVisible: true,
  },
];
